import styled, { withTheme } from 'styled-components';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';

import { FovModal, VerticalSpacer } from 'components';
import { FovButton } from 'components/generic';
import { impersonateUser } from 'store/actions/admin';
import { startImpersonation, getColor } from 'utils';

const InfoText = styled('div')`
    max-width: 26rem;
    font-size: 0.9rem;
    line-height: 1.4;
    color: ${props => getColor(props, 'onPrimary')};
`;

const ButtonRow = styled('div')`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;
`;

const PrimaryButtonWrap = styled('div')`
    min-width: 10rem;
`;

const SecondaryButton = styled('button')`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2.75rem;
    min-width: 7rem;
    padding: 0 1.5rem;
    border-radius: 0.25rem;
    border: 1px solid ${props => getColor(props, 'mediumGrey')};
    background-color: transparent;
    color: ${props => getColor(props, 'onPrimary')};
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${props => getColor(props, 'lightGrey')};
    }

    &:focus {
        outline: none;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

const ImpersonateConfirm = withTheme(props => {
    const { theme, isVisible, onClose, user } = props;
    const dispatch = useDispatch();
    const [ cookies, setCookie ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const selectedTheme = useSelector(state => state.userData.userPreferences.theme);
    const [ submitting, setSubmitting ] = useState(false);

    const userName = [ user?.first_name, user?.last_name ].filter(Boolean).join(' ') || user?.email || '';

    const handleConfirm = async () => {
        setSubmitting(true);
        const session = await dispatch(impersonateUser(token, user.id));
        setSubmitting(false);

        // On failure the action already surfaced an error message — just close.
        if (!session?.access_token) {
            onClose();
            return;
        }

        startImpersonation({ setCookie, session, user });
    };

    return (
        <FovModal
            title='Log in as user'
            visible={isVisible}
            onClose={onClose}
            maxWidth={480}
        >
            <InfoText theme={theme} $selectedTheme={selectedTheme}>
                You are about to log in as <strong>{userName}</strong>
                {user?.email ? ` (${user.email})` : ''}. Your admin session will be paused, and you
                can return to it any time from the banner at the top of the page.
            </InfoText>

            <VerticalSpacer size={20} />

            <ButtonRow>
                <SecondaryButton
                    theme={theme}
                    $selectedTheme={selectedTheme}
                    onClick={onClose}
                    disabled={submitting}
                >
                    Cancel
                </SecondaryButton>
                <PrimaryButtonWrap>
                    <FovButton
                        text={submitting ? 'Logging in…' : 'Log in as user'}
                        callback={handleConfirm}
                        disabled={submitting}
                    />
                </PrimaryButtonWrap>
            </ButtonRow>
        </FovModal>
    );
});

export { ImpersonateConfirm };
